/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { FeedbackContext } from './FeedbackContext';
import { FeedbackProps } from '../types';
import type { FeedbackComponentType, ScreenDetails } from '../types';
// Lazy (see lazyFeedback.ts): this provider mounts on every page, so a static
// import would ship the feedback UI in every web app's shared bundle.
import Feedback from '../pages/Feedback/lazyFeedback';
import ScreenshotOverlay from '../components/ScreenshotOverlay/ScreenshotOverlay';
import { ScreenshotFeedbackListener } from '../components/ScreenshotFeedbackListener/ScreenshotFeedbackListener';

export interface FeedbackProviderProps {
  initialProps?: FeedbackProps;
  children: React.ReactNode;
  componentType?: FeedbackComponentType;
  /** Optional: when provided, screenshot detection is enabled (listener is rendered inside the provider). */
  getScreenDetails?: (registered: ScreenDetails | null) => ScreenDetails | null;
  /** Optional: called when a screen sets or clears details via `useFeedbackScreenDetails`. */
  onScreenDetailsChange?: (details: ScreenDetails | null) => void;
  /** Optional: called when isFeedbackOpen changes. */
  onFeedbackOpenChange?: (isFeedbackOpen: boolean) => void;
  /** Optional: called with openFeedback so the parent can open feedback without context. */
  onOpenFeedback?: (
    openFeedback: (overrides?: Partial<FeedbackProps>) => void,
  ) => void;
  /** Optional: called with closeFeedback so the parent can close feedback without context. */
  onCloseFeedback?: (closeFeedback: () => void) => void;
  /**
   * When true, guests may submit standalone feedback and show the guest support footer.
   * Default false — existing consumers stay logged-in-only without passing this.
   */
  enableGuestFeedback?: boolean;
  /**
   * Web only: opener from the host app (e.g. `useAskBoChatBot` in `@airasia-rn/web`).
   * On native, guest support falls back to `thankyou[].avaUrl` when AskBo is not ready.
   */
  openChatWithBo?: (workflowId?: string) => void;
  /** Whether the host-provided AskBo chatbot script is loaded and configured. */
  isAskBoReady?: boolean;
}

export function FeedbackProvider({
  initialProps,
  children,
  componentType,
  getScreenDetails,
  onScreenDetailsChange,
  onFeedbackOpenChange,
  onOpenFeedback,
  onCloseFeedback,
  enableGuestFeedback = false,
  openChatWithBo,
  isAskBoReady,
}: FeedbackProviderProps) {
  const effectiveInitialProps = React.useMemo<FeedbackProps>(
    () => ({
      feedbackModalCloseCallback:
        initialProps?.feedbackModalCloseCallback ?? (() => {}),
      ...initialProps,
      ...(componentType != null && { componentType }),
    }),
    [initialProps, componentType],
  );
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [currentProps, setCurrentProps] = useState<FeedbackProps>(
    effectiveInitialProps,
  );
  const [screenshotOverlayVisible, setScreenshotOverlayVisible] =
    useState(false);
  const [screenshotScreenDetails, setScreenshotScreenDetails] =
    useState<ScreenDetails | null>(null);
  const [screenshotUri, setScreenshotUri] = useState<string | null>(null);
  const screenDetailsRef = useRef<ScreenDetails | null>(null);
  const onScreenDetailsChangeRef = useRef(onScreenDetailsChange);
  onScreenDetailsChangeRef.current = onScreenDetailsChange;
  const setScreenDetails = useCallback((details: ScreenDetails) => {
    screenDetailsRef.current = details;
    onScreenDetailsChangeRef.current?.(details);
  }, []);
  const clearScreenDetails = useCallback((details: ScreenDetails) => {
    if (screenDetailsRef.current !== details) return;
    screenDetailsRef.current = null;
    onScreenDetailsChangeRef.current?.(null);
  }, []);

  const showScreenshotOverlay = useCallback(
    (screenDetails: ScreenDetails, screenshotUriOverride?: string | null) => {
      setScreenshotScreenDetails(screenDetails);
      setScreenshotUri(screenshotUriOverride ?? null);
      setScreenshotOverlayVisible(true);
    },
    [],
  );

  const hideScreenshotOverlay = useCallback(() => {
    setScreenshotOverlayVisible(false);
    setScreenshotScreenDetails(null);
    setScreenshotUri(null);
  }, []);

  const openFeedback = useCallback(
    (overrides?: Partial<FeedbackProps>) => {
      const userClose =
        overrides?.feedbackModalCloseCallback ??
        effectiveInitialProps.feedbackModalCloseCallback;
      const merged: FeedbackProps = {
        ...effectiveInitialProps,
        ...overrides,
        feedbackModalCloseCallback: () => {
          setIsFeedbackOpen(false);
          userClose?.();
        },
      };
      setCurrentProps(merged);
      setIsFeedbackOpen(true);
    },
    [effectiveInitialProps],
  );

  const closeFeedback = useCallback(() => {
    setIsFeedbackOpen(false);
    currentProps.feedbackModalCloseCallback?.();
  }, [currentProps.feedbackModalCloseCallback]);

  useEffect(() => {
    onFeedbackOpenChange?.(isFeedbackOpen);
  }, [onFeedbackOpenChange, isFeedbackOpen]);

  useEffect(() => {
    onOpenFeedback?.(openFeedback);
  }, [onOpenFeedback, openFeedback]);

  useEffect(() => {
    onCloseFeedback?.(closeFeedback);
  }, [onCloseFeedback, closeFeedback]);

  const value = React.useMemo(
    () => ({
      translation: null,
      setTranslation: () => {},
      initialProps: currentProps,
      setInitialProps: () => {},
      isFeedbackOpen,
      openFeedback,
      closeFeedback,
      showScreenshotOverlay,
      hideScreenshotOverlay,
      isScreenshotOverlayVisible: screenshotOverlayVisible,
      enableGuestFeedback,
      openChatWithBo,
      isAskBoReady,
      screenDetailsRef,
      setScreenDetails,
      clearScreenDetails,
    }),
    [
      currentProps,
      isFeedbackOpen,
      openFeedback,
      closeFeedback,
      showScreenshotOverlay,
      hideScreenshotOverlay,
      screenshotOverlayVisible,
      enableGuestFeedback,
      openChatWithBo,
      isAskBoReady,
      screenDetailsRef,
      setScreenDetails,
      clearScreenDetails,
    ],
  );

  const handleScreenshotOverlayPress = useCallback(() => {
    hideScreenshotOverlay();

    setTimeout(() => {
      openFeedback({
        ...(componentType != null && { componentType }),
        ...(screenshotScreenDetails != null && {
          screenDetails: screenshotScreenDetails,
        }),
        screenshotImage: screenshotUri ?? undefined,
      });
    }, 100);
  }, [
    componentType,
    screenshotScreenDetails,
    screenshotUri,
    openFeedback,
    hideScreenshotOverlay,
  ]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      {getScreenDetails != null && (
        <ScreenshotFeedbackListener getScreenDetails={getScreenDetails} />
      )}
      <ScreenshotOverlay
        screenshotData={screenshotUri}
        isVisible={screenshotOverlayVisible}
        onPress={handleScreenshotOverlayPress}
        onClose={hideScreenshotOverlay}
      />
      {isFeedbackOpen && (
        <View
          style={styles.overlay}
          pointerEvents="box-none"
          collapsable={false}
        >
          <Suspense fallback={null}>
            <Feedback {...currentProps} />
          </Suspense>
        </View>
      )}
    </FeedbackContext.Provider>
  );
}

const styles = StyleSheet.create(() => ({
  overlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 9999,
    elevation: 9999,
  },
}));
